import { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { MapPin, Play, Loader2, CheckCircle, AlertCircle, RefreshCw, StopCircle, Download } from 'lucide-react';
import { cutoverApi } from '../../services/cutoverApi';
import type { GeocodingBatchStatusDto } from '../../types/cutover';
import './GeocodingTab.css';

/** Trigger a browser download for an Excel blob. */
function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function ClinicGeocodingSection() {
  const [batchSize, setBatchSize] = useState<number | ''>('');
  const [isPolling, setIsPolling] = useState(false);
  const [lastStatus, setLastStatus] = useState<GeocodingBatchStatusDto | null>(null);

  // Track the startedAt from *before* we clicked Run, so we can detect when
  // the server reports a new job (different startedAt).
  const prevStartedAtRef = useRef<string | null>(null);
  const sawNewJobRef = useRef(false);

  // Preview — how many addresses need geocoding
  const { data: preview, isLoading: loadingPreview, refetch: refetchPreview } = useQuery({
    queryKey: ['geocoding-preview'],
    queryFn: () => cutoverApi.getGeocodingPreview(),
  });

  // Status polling — active while job is running OR server says running on mount
  const { data: status, refetch: refetchStatus } = useQuery({
    queryKey: ['geocoding-status'],
    queryFn: () => cutoverApi.getGeocodingStatus(),
    refetchInterval: isPolling ? 1000 : false,
  });

  // On mount: if the server reports a running job, resume polling and show progress
  const hasResumedRef = useRef(false);
  /* eslint-disable react-hooks/set-state-in-effect */
  useEffect(() => {
    if (!status || hasResumedRef.current) return;
    hasResumedRef.current = true;
    if (status.isRunning) {
      sawNewJobRef.current = true;
      prevStartedAtRef.current = null;
      setLastStatus(status);
      setIsPolling(true);
    }
  }, [status]);
  /* eslint-enable react-hooks/set-state-in-effect */

  // Track status changes and stop polling when job completes
  /* eslint-disable react-hooks/set-state-in-effect */
  useEffect(() => {
    if (!status || !isPolling) return;

    // Phase 1: Wait until the server reports a job with a different startedAt
    // than what was there before we clicked Run (meaning our new job has started).
    if (!sawNewJobRef.current) {
      if (status.startedAt && status.startedAt !== prevStartedAtRef.current) {
        sawNewJobRef.current = true;
        // Fall through to update lastStatus
      } else if (status.isRunning) {
        // Server says running — even if startedAt hasn't changed yet, trust it
        sawNewJobRef.current = true;
      } else {
        // Still seeing stale status from before our run — keep polling, don't update UI
        return;
      }
    }

    // Phase 2: We've confirmed our job started. Update UI with progress.
    setLastStatus(status);

    if (!status.isRunning) {
      // Job finished
      setIsPolling(false);
      void refetchPreview();
    }
  }, [status, isPolling, refetchPreview]);
  /* eslint-enable react-hooks/set-state-in-effect */

  // Start the batch job
  const runMutation = useMutation({
    mutationFn: () => cutoverApi.runGeocoding(batchSize === '' ? undefined : batchSize),
    onSuccess: () => {
      // Capture the current startedAt so we can detect when a new job appears
      prevStartedAtRef.current = status?.startedAt ?? null;
      sawNewJobRef.current = false;
      setLastStatus(null);
      setIsPolling(true);
      void refetchStatus();
    },
  });

  const stopMutation = useMutation({
    mutationFn: () => cutoverApi.stopGeocoding(),
  });

  // Cutover C7: export the exception list — every clinic still lacking coordinates
  const exportMutation = useMutation({
    mutationFn: () => cutoverApi.exportGeocodingExceptions(),
    onSuccess: (blob) => downloadBlob(blob, `Ungeocoded_Clinics_${new Date().toISOString().slice(0, 10)}.xlsx`),
  });

  const isRunning = isPolling || (status?.isRunning ?? false);
  const showProgress = isPolling || (lastStatus && !lastStatus.isRunning && lastStatus.processed > 0);
  const jobComplete = lastStatus && !lastStatus.isRunning && lastStatus.processed > 0;
  // A run also counts as complete when the server reports one after a page reload,
  // where lastStatus is never populated (it only fills while polling a live run).
  const runComplete = !isPolling && Boolean(jobComplete || (status && !status.isRunning && status.processed > 0));

  const formatDuration = (start: string | null, end: string | null) => {
    if (!start || !end) return null;
    const ms = new Date(end).getTime() - new Date(start).getTime();
    if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
    const mins = Math.floor(ms / 60000);
    const secs = Math.floor((ms % 60000) / 1000);
    return `${mins}m ${secs}s`;
  };

  const progressPct = lastStatus && lastStatus.total > 0
    ? Math.round((lastStatus.processed / lastStatus.total) * 100)
    : 0;

  return (
    <div className="geo-tab">
      <div className="geo-header">
        <MapPin size={18} />
        <div>
          <h2 className="geo-title">Clinic Address Geocoding</h2>
          <p className="geo-desc">
            Populate missing latitude/longitude coordinates for clinic addresses by calling
            the Google Geocoding API. This enables the Clinic Proximity Search and Client
            Relationships map to show all clinics.
          </p>
        </div>
      </div>

      {/* Preview card */}
      <div className="geo-preview-card">
        {loadingPreview ? (
          <div className="geo-preview-loading">
            <Loader2 size={16} className="geo-spinning" />
            <span>Checking address data…</span>
          </div>
        ) : preview ? (
          <div className="geo-preview-stats">
            <div className="geo-stat">
              <span className="geo-stat-value">{preview.totalUngeocoded.toLocaleString()}</span>
              <span className="geo-stat-label">Addresses need geocoding</span>
            </div>
            <div className="geo-stat-sep" />
            <div className="geo-stat">
              <span className="geo-stat-value">{preview.totalClinics.toLocaleString()}</span>
              <span className="geo-stat-label">Clinics affected</span>
            </div>
          </div>
        ) : (
          <span className="geo-preview-error">Could not load preview data.</span>
        )}
      </div>

      {/* Controls */}
      <div className="geo-controls">
        <div className="geo-batch-field">
          <label htmlFor="batch-size" className="geo-label">
            Batch size <span className="geo-label-hint">(leave blank to geocode all)</span>
          </label>
          <input
            id="batch-size"
            type="number"
            min={1}
            max={10000}
            className="geo-batch-input"
            placeholder="e.g. 100"
            value={batchSize}
            onChange={(e) => setBatchSize(e.target.value === '' ? '' : Number(e.target.value))}
            disabled={isRunning}
          />
        </div>

        <button
          type="button"
          className="geo-btn geo-btn-primary"
          onClick={() => runMutation.mutate()}
          disabled={isRunning || runMutation.isPending || (preview?.totalUngeocoded === 0)}
        >
          {isRunning || runMutation.isPending ? (
            <><Loader2 size={15} className="geo-spinning" /> Running…</>
          ) : (
            <><Play size={15} /> Run Geocoding</>
          )}
        </button>

        {isRunning && (
          <button
            type="button"
            className="geo-btn geo-btn-danger"
            onClick={() => stopMutation.mutate()}
            disabled={stopMutation.isPending}
          >
            <StopCircle size={15} /> Stop
          </button>
        )}

        {runComplete && (
          <button
            type="button"
            className="geo-btn geo-btn-secondary"
            onClick={() => { void refetchPreview(); void refetchStatus(); }}
          >
            <RefreshCw size={15} /> Refresh
          </button>
        )}

        {runComplete && (
          <button
            type="button"
            className="geo-btn geo-btn-secondary"
            title="Excel of the exceptions from geocoding: attempted-and-failed clinics plus clinics with no usable address"
            onClick={() => exportMutation.mutate()}
            disabled={exportMutation.isPending}
          >
            {exportMutation.isPending ? (
              <><Loader2 size={15} className="geo-spinning" /> Exporting…</>
            ) : (
              <><Download size={15} /> Export Exceptions</>
            )}
          </button>
        )}
      </div>

      {preview?.totalUngeocoded === 0 && !isRunning && !showProgress && (
        <div className="geo-all-done">
          <CheckCircle size={16} />
          All clinic addresses already have coordinates — nothing to do.
        </div>
      )}

      {/* Progress */}
      {showProgress && lastStatus && (
        <div className="geo-progress-section">
          <div className="geo-progress-header">
            <span className="geo-progress-label">
              {lastStatus.isRunning ? 'Geocoding in progress…' : 'Geocoding complete'}
            </span>
            <span className="geo-progress-counts">
              {lastStatus.processed.toLocaleString()} / {lastStatus.total.toLocaleString()}
            </span>
          </div>

          <div className="geo-progress-bar-track">
            <div
              className="geo-progress-bar-fill"
              style={{ width: `${progressPct}%` }}
            />
          </div>

          <div className="geo-progress-stats">
            <span className="geo-stat-success">
              <CheckCircle size={13} /> {lastStatus.succeeded.toLocaleString()} succeeded
            </span>
            {lastStatus.failed > 0 && (
              <span className="geo-stat-failure">
                <AlertCircle size={13} /> {lastStatus.failed.toLocaleString()} failed
              </span>
            )}
            {jobComplete && lastStatus.startedAt && lastStatus.completedAt && (
              <span className="geo-stat-duration">
                {formatDuration(lastStatus.startedAt, lastStatus.completedAt)}
              </span>
            )}
          </div>

          {lastStatus.lastError && (
            <div className="geo-last-error">
              <AlertCircle size={13} />
              <span>Last error: {lastStatus.lastError}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function LocationGeocodingSection() {
  const [batchSize, setBatchSize] = useState<number | ''>('');
  const [isPolling, setIsPolling] = useState(false);
  const [lastStatus, setLastStatus] = useState<GeocodingBatchStatusDto | null>(null);

  const prevStartedAtRef = useRef<string | null>(null);
  const sawNewJobRef = useRef(false);

  const { data: preview, isLoading: loadingPreview, refetch: refetchPreview } = useQuery({
    queryKey: ['location-geocoding-preview'],
    queryFn: () => cutoverApi.getLocationGeocodingPreview(),
  });

  const { data: status, refetch: refetchStatus } = useQuery({
    queryKey: ['location-geocoding-status'],
    queryFn: () => cutoverApi.getLocationGeocodingStatus(),
    refetchInterval: isPolling ? 1000 : false,
  });

  const hasResumedRef = useRef(false);
  /* eslint-disable react-hooks/set-state-in-effect */
  useEffect(() => {
    if (!status || hasResumedRef.current) return;
    hasResumedRef.current = true;
    if (status.isRunning) {
      sawNewJobRef.current = true;
      prevStartedAtRef.current = null;
      setLastStatus(status);
      setIsPolling(true);
    }
  }, [status]);
  /* eslint-enable react-hooks/set-state-in-effect */

  /* eslint-disable react-hooks/set-state-in-effect */
  useEffect(() => {
    if (!status || !isPolling) return;

    if (!sawNewJobRef.current) {
      if (status.startedAt && status.startedAt !== prevStartedAtRef.current) {
        sawNewJobRef.current = true;
      } else if (status.isRunning) {
        sawNewJobRef.current = true;
      } else {
        return;
      }
    }

    setLastStatus(status);

    if (!status.isRunning) {
      setIsPolling(false);
      void refetchPreview();
    }
  }, [status, isPolling, refetchPreview]);
  /* eslint-enable react-hooks/set-state-in-effect */

  const runMutation = useMutation({
    mutationFn: () => cutoverApi.runLocationGeocoding(batchSize === '' ? undefined : batchSize),
    onSuccess: () => {
      prevStartedAtRef.current = status?.startedAt ?? null;
      sawNewJobRef.current = false;
      setLastStatus(null);
      setIsPolling(true);
      void refetchStatus();
    },
  });

  const stopMutation = useMutation({
    mutationFn: () => cutoverApi.stopLocationGeocoding(),
  });

  // Cutover C7: export the exception list — the priority target per the Aug 4 review
  const exportMutation = useMutation({
    mutationFn: () => cutoverApi.exportLocationGeocodingExceptions(),
    onSuccess: (blob) => downloadBlob(blob, `Ungeocoded_CustomerLocations_${new Date().toISOString().slice(0, 10)}.xlsx`),
  });

  const isRunning = isPolling || (status?.isRunning ?? false);
  const showProgress = isPolling || (lastStatus && !lastStatus.isRunning && lastStatus.processed > 0);
  const jobComplete = lastStatus && !lastStatus.isRunning && lastStatus.processed > 0;
  // A run also counts as complete when the server reports one after a page reload,
  // where lastStatus is never populated (it only fills while polling a live run).
  const runComplete = !isPolling && Boolean(jobComplete || (status && !status.isRunning && status.processed > 0));

  const formatDuration = (start: string | null, end: string | null) => {
    if (!start || !end) return null;
    const ms = new Date(end).getTime() - new Date(start).getTime();
    if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
    const mins = Math.floor(ms / 60000);
    const secs = Math.floor((ms % 60000) / 1000);
    return `${mins}m ${secs}s`;
  };

  const progressPct = lastStatus && lastStatus.total > 0
    ? Math.round((lastStatus.processed / lastStatus.total) * 100)
    : 0;

  return (
    <div className="geo-tab">
      <div className="geo-header">
        <MapPin size={18} />
        <div>
          <h2 className="geo-title">Customer Location Geocoding</h2>
          <p className="geo-desc">
            Populate missing latitude/longitude coordinates for customer location addresses by calling
            the Google Geocoding API. This enables the Client Coverage page to calculate nearby clinic
            counts for each location.
          </p>
        </div>
      </div>

      <div className="geo-preview-card">
        {loadingPreview ? (
          <div className="geo-preview-loading">
            <Loader2 size={16} className="geo-spinning" />
            <span>Checking location data...</span>
          </div>
        ) : preview ? (
          <div className="geo-preview-stats">
            <div className="geo-stat">
              <span className="geo-stat-value">{preview.totalUngeocoded.toLocaleString()}</span>
              <span className="geo-stat-label">Locations need geocoding</span>
            </div>
          </div>
        ) : (
          <span className="geo-preview-error">Could not load preview data.</span>
        )}
      </div>

      <div className="geo-controls">
        <div className="geo-batch-field">
          <label htmlFor="loc-batch-size" className="geo-label">
            Batch size <span className="geo-label-hint">(leave blank to geocode all)</span>
          </label>
          <input
            id="loc-batch-size"
            type="number"
            min={1}
            max={10000}
            className="geo-batch-input"
            placeholder="e.g. 500"
            value={batchSize}
            onChange={(e) => setBatchSize(e.target.value === '' ? '' : Number(e.target.value))}
            disabled={isRunning}
          />
        </div>

        <button
          type="button"
          className="geo-btn geo-btn-primary"
          onClick={() => runMutation.mutate()}
          disabled={isRunning || runMutation.isPending || (preview?.totalUngeocoded === 0)}
        >
          {isRunning || runMutation.isPending ? (
            <><Loader2 size={15} className="geo-spinning" /> Running...</>
          ) : (
            <><Play size={15} /> Run Geocoding</>
          )}
        </button>

        {isRunning && (
          <button
            type="button"
            className="geo-btn geo-btn-danger"
            onClick={() => stopMutation.mutate()}
            disabled={stopMutation.isPending}
          >
            <StopCircle size={15} /> Stop
          </button>
        )}

        {runComplete && (
          <button
            type="button"
            className="geo-btn geo-btn-secondary"
            onClick={() => { void refetchPreview(); void refetchStatus(); }}
          >
            <RefreshCw size={15} /> Refresh
          </button>
        )}

        {runComplete && (
          <button
            type="button"
            className="geo-btn geo-btn-secondary"
            title="Excel of the exceptions from geocoding: attempted-and-failed locations plus locations with no usable address, with customer names and XMIDs"
            onClick={() => exportMutation.mutate()}
            disabled={exportMutation.isPending}
          >
            {exportMutation.isPending ? (
              <><Loader2 size={15} className="geo-spinning" /> Exporting…</>
            ) : (
              <><Download size={15} /> Export Exceptions</>
            )}
          </button>
        )}
      </div>

      {preview?.totalUngeocoded === 0 && !isRunning && !showProgress && (
        <div className="geo-all-done">
          <CheckCircle size={16} />
          All customer locations already have coordinates — nothing to do.
        </div>
      )}

      {showProgress && lastStatus && (
        <div className="geo-progress-section">
          <div className="geo-progress-header">
            <span className="geo-progress-label">
              {lastStatus.isRunning ? 'Geocoding in progress...' : 'Geocoding complete'}
            </span>
            <span className="geo-progress-counts">
              {lastStatus.processed.toLocaleString()} / {lastStatus.total.toLocaleString()}
            </span>
          </div>

          <div className="geo-progress-bar-track">
            <div
              className="geo-progress-bar-fill"
              style={{ width: `${progressPct}%` }}
            />
          </div>

          <div className="geo-progress-stats">
            <span className="geo-stat-success">
              <CheckCircle size={13} /> {lastStatus.succeeded.toLocaleString()} succeeded
            </span>
            {lastStatus.failed > 0 && (
              <span className="geo-stat-failure">
                <AlertCircle size={13} /> {lastStatus.failed.toLocaleString()} failed
              </span>
            )}
            {jobComplete && lastStatus.startedAt && lastStatus.completedAt && (
              <span className="geo-stat-duration">
                {formatDuration(lastStatus.startedAt, lastStatus.completedAt)}
              </span>
            )}
          </div>

          {lastStatus.lastError && (
            <div className="geo-last-error">
              <AlertCircle size={13} />
              <span>Last error: {lastStatus.lastError}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function GeocodingTab() {
  return (
    <>
      <ClinicGeocodingSection />
      <LocationGeocodingSection />
    </>
  );
}
